/**
 * useReconnection - Hook for managing reconnection state
 * Implements exponential backoff per VIDEO_ROOM.md spec
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Room, RoomEvent, ConnectionState } from "livekit-client";

export interface ReconnectionConfig {
  initialDelayMs: number;
  maxDelayMs: number;
  maxAttempts: number;
  backoffMultiplier: number;
  jitterMs: number;
}

const DEFAULT_CONFIG: ReconnectionConfig = {
  initialDelayMs: 500,
  maxDelayMs: 10000,
  maxAttempts: 15,
  backoffMultiplier: 1.5,
  jitterMs: 200,
};

export interface UseReconnectionOptions {
  room: Room | null;
  config?: Partial<ReconnectionConfig>;
  onReconnectFailed?: () => void;
}

export interface UseReconnectionReturn {
  isReconnecting: boolean;
  attempt: number;
  maxAttempts: number;
  canManualRetry: boolean;
  manualReconnect: () => Promise<void>;
  lastError: string | null;
  timeUntilNextAttempt: number | null;
}

export function useReconnection(
  options: UseReconnectionOptions
): UseReconnectionReturn {
  const { room, config: configOverrides, onReconnectFailed } = options;
  const config = { ...DEFAULT_CONFIG, ...configOverrides };

  const [isReconnecting, setIsReconnecting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [canManualRetry, setCanManualRetry] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [timeUntilNextAttempt] = useState<number | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Save local state on disconnect
  const saveLocalState = useCallback(() => {
    if (!room?.localParticipant) return;

    const state = {
      audioEnabled: room.localParticipant.isMicrophoneEnabled,
      videoEnabled: room.localParticipant.isCameraEnabled,
      timestamp: Date.now(),
    };
    sessionStorage.setItem("videoRoomState", JSON.stringify(state));
  }, [room]);

  useEffect(() => {
    if (!room) return;

    const handleReconnecting = () => {
      setIsReconnecting(true);
      setAttempt((prev) => prev + 1);
      saveLocalState();
    };

    const handleReconnected = () => {
      setIsReconnecting(false);
      setAttempt(0);
      setLastError(null);
      setCanManualRetry(false);
    };

    const handleDisconnected = () => {
      if (attempt >= config.maxAttempts) {
        setIsReconnecting(false);
        setCanManualRetry(true);
        setLastError("Verbindung fehlgeschlagen nach " + config.maxAttempts + " Versuchen");
        onReconnectFailed?.();
      }
    };

    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);

    return () => {
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
    };
  }, [room, attempt, config.maxAttempts, saveLocalState, onReconnectFailed]);

  const manualReconnect = useCallback(async () => {
    if (!room) return;

    setIsReconnecting(true);
    setAttempt(1);
    setCanManualRetry(false);
    setLastError(null);

    try {
      // Room will handle reconnection internally
      // This triggers a fresh connection attempt
      if (room.state === ConnectionState.Disconnected) {
        // Note: In practice, you'd need to get a fresh token here
        // This is a simplified version
        setLastError("Bitte laden Sie die Seite neu für eine neue Verbindung");
        setCanManualRetry(true);
      }
    } catch {
      setLastError("Wiederverbindung fehlgeschlagen");
      setCanManualRetry(true);
    } finally {
      setIsReconnecting(false);
    }
  }, [room]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    isReconnecting,
    attempt,
    maxAttempts: config.maxAttempts,
    canManualRetry,
    manualReconnect,
    lastError,
    timeUntilNextAttempt,
  };
}
